import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ICONS,
  Icon,
  cn,
  type IconName,
} from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { AgentStatusBadge } from "../shared/agent-status";
import { useBuildFormatters } from "../shared/format";
import type { AgentSummary } from "../shared/types";
import { CHANNEL_ICONS, agentAudience } from "./agent-model";

export interface AgentCardPermissions {
  create: boolean;
  edit: boolean;
  publish: boolean;
  remove: boolean;
}

interface AgentCardProps {
  agent: AgentSummary;
  audienceLabel: (code: number) => string;
  can: AgentCardPermissions;
  onDuplicate: (agent: AgentSummary) => void;
  onToggleStatus: (agent: AgentSummary) => void;
  onPublish: (agent: AgentSummary) => void;
  onDelete: (agent: AgentSummary) => void;
}

function iconOf(agent: AgentSummary): IconName {
  const name = agent.icon ?? "";
  return name in ICONS ? (name as IconName) : "bot";
}

function AgentMark({ agent }: { agent: AgentSummary }) {
  const live = agent.status === "live";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex size-11 shrink-0 items-center justify-center rounded-lg",
        live ? "bg-brand-500 text-ink-950" : "bg-sunken text-fg-muted",
      )}
    >
      <Icon name={iconOf(agent)} className="size-5" />
      {live ? <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-pill bg-mint-500 ring-2 ring-surface" /> : null}
    </span>
  );
}

function AgentMenu({ agent, can, onDuplicate, onToggleStatus, onPublish, onDelete }: Omit<AgentCardProps, "audienceLabel">) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canToggle = agent.status === "live" ? can.edit : can.publish;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={t("build.agents.card.moreActions", { name: agent.name })}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {agent.status === "draft" && can.publish ? (
          <DropdownMenuItem onSelect={() => { onPublish(agent); }}>
            <Icon name="play" />
            {t("build.agents.card.publish")}
          </DropdownMenuItem>
        ) : null}
        {can.create ? (
          <DropdownMenuItem onSelect={() => { onDuplicate(agent); }}>
            <Icon name="copy" />
            {t("build.agents.card.duplicate")}
          </DropdownMenuItem>
        ) : null}
        {agent.status !== "draft" && canToggle ? (
          <DropdownMenuItem onSelect={() => { onToggleStatus(agent); }}>
            <Icon name={agent.status === "live" ? "pause" : "play"} />
            {t(agent.status === "live" ? "build.agents.card.pause" : "build.agents.card.resume")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem asChild>
          <AppLink href={consoleRoutes.agentSettings(summary.slug, agent.id)}>
            <Icon name="settings" />
            {t("build.agents.card.settings")}
          </AppLink>
        </DropdownMenuItem>
        {can.remove ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem tone="danger" onSelect={() => { onDelete(agent); }}>
              <Icon name="trash" />
              {t("build.agents.card.delete")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Stats({ agent }: { agent: AgentSummary }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const deployed = agent.channels.length > 0;
  const cells = [
    { label: t("build.agents.card.convos"), value: deployed ? fmt.number(agent.stats.conversations_7d) : "—" },
    { label: t("build.agents.card.resolved"), value: fmt.percent(agent.stats.resolution_rate) },
    { label: t("build.agents.card.sources"), value: fmt.number(agent.stats.sources_count) },
  ];
  return (
    <dl className="grid grid-cols-3 gap-2 text-center">
      {cells.map((cell) => (
        <div key={cell.label} className="rounded-md bg-surface-2 py-2.5">
          <dt className="text-[11px] font-semibold text-fg-subtle">{cell.label}</dt>
          <dd className={cn("text-base font-extrabold tabular-nums", cell.value === "—" && "text-fg-subtle")}>{cell.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Training({ agent }: { agent: AgentSummary }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const processing = agent.stats.processing_sources;
  const trained = agent.last_trained_at ? fmt.ago(agent.last_trained_at) : t("build.agents.card.neverTrained");
  return (
    <dl className="space-y-1.5 text-xs">
      <div className="flex justify-between gap-3">
        <dt className="text-fg-subtle">{t("build.agents.card.model")}</dt>
        <dd className="truncate font-semibold">{agent.model ?? t("build.agents.card.modelDefault")}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-fg-subtle">{t("build.agents.card.lastTrained")}</dt>
        <dd className="truncate text-right font-semibold">
          {processing > 0 ? `${trained} · ${t("build.agents.card.processing", { count: processing })}` : trained}
        </dd>
      </div>
    </dl>
  );
}

/** One agent in the grid (mockup `article.card`): identity, audience, channels, 7-day stats, actions. */
export function AgentCard(props: AgentCardProps) {
  const { agent, audienceLabel } = props;
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const audience = agentAudience(agent.audiences, audienceLabel, (label) => t("build.agents.card.audienceOnly", { label }));
  return (
    <article className="card flex flex-col" data-testid="agent-card" aria-labelledby={`agent-${agent.id}`}>
      <div className="flex items-start gap-3 p-5 pb-4">
        <AgentMark agent={agent} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id={`agent-${agent.id}`} className="text-base font-bold">
              {agent.name}
            </h2>
            <AgentStatusBadge status={agent.status} />
          </div>
          {agent.description ? <p className="mt-1 text-xs leading-relaxed text-fg-muted">{agent.description}</p> : null}
        </div>
        <AgentMenu {...props} />
      </div>
      <div className="space-y-3 px-5 pb-5 text-sm">
        <div className="flex items-center gap-2">
          <Icon name={audience.isPublic ? "globe" : "lock"} className="size-4 text-fg-subtle" />
          <span className="whitespace-nowrap font-semibold">{audience.label}</span>
          {audience.isPublic ? <span className="min-w-0 truncate text-fg-subtle">· {t("build.agents.card.audiencePublic")}</span> : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {agent.channels.length > 0 ? (
            agent.channels.map((channel) => (
              <Badge key={channel}>
                <Icon name={CHANNEL_ICONS[channel] ?? "plug"} className="size-3" />
                {t(`build.channels.${channel}`, { defaultValue: channel })}
              </Badge>
            ))
          ) : (
            <Badge tone="warning">
              <Icon name="alert-circle" className="size-3" />
              {t("build.agents.notDeployed")}
            </Badge>
          )}
        </div>
        <Stats agent={agent} />
        <Training agent={agent} />
      </div>
      <div className="mt-auto flex items-center gap-2 border-t border-line px-5 py-3.5">
        <Button asChild size="sm" className="flex-1">
          <AppLink href={consoleRoutes.agentSettings(summary.slug, agent.id)}>
            {t("build.agents.card.open")}
            <Icon name="arrow-right" />
          </AppLink>
        </Button>
        <Button asChild size="sm" variant="secondary" className="flex-1">
          <AppLink href={consoleRoutes.playground(summary.slug, agent.id)}>
            <Icon name="messages-square" />
            {t("build.agents.card.playground")}
          </AppLink>
        </Button>
      </div>
    </article>
  );
}
